import { IconButton, IconTile } from "@design";
import { Handle, Position } from "@xyflow/react";
import { Bot, Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import type { StepNodeData } from "./flowEditorModel";

/** The custom React Flow node: one step, with hover affordances to grow the tree. */
export function StepNodeView({ id, data }: Readonly<{ id: string; data: StepNodeData }>) {
  const { t } = useTranslation("workflows");
  return (
    <div className="group relative">
      <Handle type="target" position={Position.Top} className="size-3 bg-content-tertiary" />
      <div className="min-w-[220px] max-w-[280px] rounded-lg border border-border-strong bg-surface-primary p-4 shadow-lg">
        <div className="flex items-center gap-3">
          <IconTile>
            <Bot size={16} />
          </IconTile>
          <div className="min-w-0 flex-1">
            <Text variant="body-sm" className="truncate">
              {data.label || t("editor.stepConfig")}
            </Text>
            {data.agentName && (
              <Text variant="caption" tone="tertiary" className="truncate">
                {data.agentName}
              </Text>
            )}
          </div>
        </div>
      </div>
      {!data.readOnly && (
        <>
          <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 opacity-0 transition-opacity group-hover:opacity-100">
            <IconButton
              icon={<Plus />}
              aria-label={t("editor.insertBefore")}
              size="xs"
              onClick={() => data.onInsertBefore?.(id)}
            />
          </div>
          <div className="absolute -bottom-3 left-1/2 z-10 -translate-x-1/2 opacity-0 transition-opacity group-hover:opacity-100">
            <IconButton
              icon={<Plus />}
              aria-label={t("editor.addChild")}
              size="xs"
              onClick={() => data.onAddChild?.(id)}
            />
          </div>
          <div className="absolute -top-2 -right-2 z-10 opacity-0 transition-opacity group-hover:opacity-100">
            <IconButton
              icon={<X />}
              aria-label={t("editor.removeAndChildren")}
              size="xs"
              onClick={() => data.onDelete?.(id)}
            />
          </div>
        </>
      )}
      <Handle type="source" position={Position.Bottom} className="size-3 bg-content-tertiary" />
    </div>
  );
}
